export type FollowUserType = 'oyente' | 'locutor' | 'moderador' | 'creador';
export type FollowUserTab = 'seguidores' | 'siguiendo' | 'actividad' | 'notificaciones';
export type FollowNotificationType = 'nuevo-seguidor' | 'nuevo-logro' | 'nuevo-post';

export interface FollowUser {
  id: string;
  type: FollowUserType;
  name: string;
  username: string;
  photoUrl: string;
  coverUrl: string;
  city: string;
  country: string;
  countryFlag: string;
  bio: string;
  level: number;
  levelName: string;
  followersCount: number;
  followingCount: number;
  postsCount: number;
  achievementsCount: number;
  badges: string[];
  isLive: boolean;
  isVerified: boolean;
  isFollowing: boolean;
  isBlocked: boolean;
  joinedDate: string;
  lastActive: string;
  tags: string[];
}

export interface FollowActivityItem {
  id: number;
  userId: string;
  userName: string;
  userPhoto: string;
  type: string;
  description: string;
  icon: string;
  iconColor: string;
  iconBg: string;
  timestamp: string;
  timestampRelative: string;
}

export interface FollowNotification {
  id: number;
  type: FollowNotificationType;
  fromUserId: string;
  fromName: string;
  fromPhoto: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  timestamp: string;
  isRead: boolean;
  actionUrl: string;
}

export interface FollowDashboardStats {
  totalFollows: number;
  weeklyNewFollows: number;
  avgFollowsPerUser: number;
  mutualFollows: number;
  topCity: string;
  topCountry: string;
  activeFollowUsers: number;
  totalPostsFeed: number;
}

export const followUserTypeLabels: Record<FollowUserType, string> = {
  oyente: 'Oyente',
  locutor: 'Locutor',
  moderador: 'Moderador',
  creador: 'Creador',
};

export const followUserTypeIcons: Record<FollowUserType, string> = {
  oyente: 'ri-headphone-line',
  locutor: 'ri-user-voice-line',
  moderador: 'ri-shield-check-line',
  creador: 'ri-brush-line',
};

export const followUserTypeColors: Record<FollowUserType, { badge: string; ring: string; text: string }> = {
  oyente: { badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', ring: 'ring-emerald-400', text: 'text-emerald-600' },
  locutor: { badge: 'bg-amber-100 text-amber-700 border-amber-200', ring: 'ring-amber-400', text: 'text-amber-600' },
  moderador: { badge: 'bg-rose-100 text-rose-700 border-rose-200', ring: 'ring-rose-400', text: 'text-rose-600' },
  creador: { badge: 'bg-violet-100 text-violet-700 border-violet-200', ring: 'ring-violet-400', text: 'text-violet-600' },
};

export const followDashboardStats: FollowDashboardStats = {
  totalFollows: 892400,
  weeklyNewFollows: 12800,
  avgFollowsPerUser: 18.7,
  mutualFollows: 234600,
  topCity: 'CDMX',
  topCountry: 'México',
  activeFollowUsers: 67400,
  totalPostsFeed: 143200,
};

export const followUsers: FollowUser[] = [
  // ═══════ OYENTES (8) ═══════
  {
    id: 'oyente-1',
    type: 'oyente',
    name: 'Marco Antonio Rivera',
    username: '@marco_oyente',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20confident%20Latino%20male%20portrait%20warm%20genuine%20friendly%20smile%20casual%20olive%20green%20henley%20shirt%20soft%20warm%20cream%20background%20natural%20lighting%20approachable%20authentic%20headshot&width=400&height=400&seq=fu-oyente-01&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Warm%20gradient%20abstract%20background%20soft%20overlapping%20circles%20flowing%20organic%20shapes%20coral%20pink%20amber%20gold%20warm%20cream%20tones%20elegant%20modern%20social%20media%20profile%20cover%20banner%20subtle%20depth%20gentle%20glow&width=1600&height=500&seq=fu-cover-oyente-01&orientation=landscape',
    city: 'Ciudad de México',
    country: 'México',
    countryFlag: '🇲🇽',
    bio: 'Amante de la música regional mexicana y la buena radio. Oyente fiel desde 2020. Siempre con la radio encendida mientras trabajo.',
    level: 4,
    levelName: 'Top Fan',
    followersCount: 1247,
    followingCount: 28,
    postsCount: 342,
    achievementsCount: 12,
    badges: ['Top Fan', 'VIP', 'Embajador'],
    isLive: false,
    isVerified: false,
    isFollowing: false,
    isBlocked: false,
    joinedDate: '2020-03-15',
    lastActive: 'Ahora',
    tags: ['Regional', 'Rock', 'Comunidad'],
  },
  {
    id: 'oyente-2',
    type: 'oyente',
    name: 'Sofía Hernández',
    username: '@sofia_oyente',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20portrait%20friendly%20warm%20smile%20casual%20modern%20style%20soft%20warm%20neutral%20background%20editorial%20headshot%20clean%20professional%20photography&width=400&height=400&seq=fu-oyente-02&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Soft%20pastel%20gradient%20abstract%20background%20elegant%20flowing%20shapes%20gentle%20pink%20lavender%20and%20peach%20tones%20modern%20feminine%20social%20media%20banner%20sophisticated%20minimal%20aesthetic%20dreamy&width=1600&height=500&seq=fu-cover-oyente-02&orientation=landscape',
    city: 'Guadalajara',
    country: 'México',
    countryFlag: '🇲🇽',
    bio: 'Futura locutora. Coleccionista de playeras de estaciones de radio. Fan de los maratones musicales de fin de semana. 🎧',
    level: 3,
    levelName: 'Fan',
    followersCount: 892,
    followingCount: 45,
    postsCount: 218,
    achievementsCount: 8,
    badges: ['Top Fan', 'VIP'],
    isLive: false,
    isVerified: false,
    isFollowing: true,
    isBlocked: false,
    joinedDate: '2021-08-22',
    lastActive: 'Hace 15 min',
    tags: ['Pop', 'Urbano', 'Futura Locutora'],
  },
  {
    id: 'oyente-3',
    type: 'oyente',
    name: 'Carlos Mendoza',
    username: '@carlos_fan',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20mid%20twenties%20casual%20style%20warm%20smile%20premium%20portrait%20photography%20soft%20neutral%20background%20editorial%20quality%20headshot%20approachable&width=400&height=400&seq=fu-oyente-03&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20abstract%20gradient%20background%20bold%20energetic%20orange%20and%20teal%20geometric%20shapes%20modern%20creative%20social%20media%20banner%20profile%20cover%20vibrant%20artistic%20minimal&width=1600&height=500&seq=fu-cover-oyente-03&orientation=landscape',
    city: 'Monterrey',
    country: 'México',
    countryFlag: '🇲🇽',
    bio: 'Deportista de corazón. No me pierdo ningún Carrusel Deportivo. Colecciono camisetas de fútbol y radios vintage.',
    level: 3,
    levelName: 'Fan',
    followersCount: 534,
    followingCount: 32,
    postsCount: 156,
    achievementsCount: 5,
    badges: ['Top Fan'],
    isLive: false,
    isVerified: false,
    isFollowing: true,
    isBlocked: false,
    joinedDate: '2022-01-10',
    lastActive: 'Hace 5 min',
    tags: ['Deportes', 'Fútbol', 'Análisis'],
  },
  {
    id: 'oyente-4',
    type: 'oyente',
    name: 'Lucía Fernández',
    username: '@lucia_radiofan',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20curly%20dark%20hair%20warm%20genuine%20smile%20artistic%20bohemian%20style%20soft%20golden%20hour%20lighting%20editorial%20portrait%20gentle%20natural%20authentic&width=400&height=400&seq=fu-oyente-04&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Artistic%20abstract%20watercolor%20background%20warm%20golden%20amber%20and%20terracotta%20tones%20flowing%20organic%20brush%20strokes%20elegant%20bohemian%20social%20media%20banner%20cover%20refined&width=1600&height=500&seq=fu-cover-oyente-04&orientation=landscape',
    city: 'Buenos Aires',
    country: 'Argentina',
    countryFlag: '🇦🇷',
    bio: 'La radio me acompaña desde que tengo memoria. Amo los talk shows de la tarde y la música indie. También escribo reseñas de programas.',
    level: 3,
    levelName: 'Fan',
    followersCount: 445,
    followingCount: 56,
    postsCount: 289,
    achievementsCount: 6,
    badges: ['Creadora', 'Comunicadora'],
    isLive: false,
    isVerified: false,
    isFollowing: false,
    isBlocked: false,
    joinedDate: '2021-03-05',
    lastActive: 'Hace 1 hora',
    tags: ['Talk Show', 'Indie', 'Reseñas'],
  },
  {
    id: 'oyente-5',
    type: 'oyente',
    name: 'Diego Martínez',
    username: '@dieguito_rfw',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20early%20twenties%20trendy%20streetwear%20style%20confident%20expression%20urban%20background%20portrait%20photography%20casual%20cool%20modern&width=400&height=400&seq=fu-oyente-05&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Urban%20nightlife%20abstract%20gradient%20background%20neon%20pink%20and%20cyan%20electric%20energy%20bold%20modern%20street%20culture%20social%20media%20banner%20youthful%20vibrant&width=1600&height=500&seq=fu-cover-oyente-05&orientation=landscape',
    city: 'Medellín',
    country: 'Colombia',
    countryFlag: '🇨🇴',
    bio: 'Reggaetón y radio urbana 24/7. La Kalle es mi segunda casa. Gané el concurso de Mejor Fan 2025.',
    level: 4,
    levelName: 'Top Fan',
    followersCount: 1023,
    followingCount: 38,
    postsCount: 187,
    achievementsCount: 9,
    badges: ['Ganador', 'Top Fan', 'VIP'],
    isLive: false,
    isVerified: false,
    isFollowing: false,
    isBlocked: false,
    joinedDate: '2020-11-20',
    lastActive: 'Hace 30 min',
    tags: ['Urbano', 'Reggaetón', 'Ganador'],
  },
  {
    id: 'oyente-6',
    type: 'oyente',
    name: 'Ana Paula Torres',
    username: '@anapaula_oye',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20professional%20elegant%20style%20warm%20smile%20pearl%20earrings%20soft%20studio%20lighting%20editorial%20portrait%20refined%20sophisticated%20approachable&width=400&height=400&seq=fu-oyente-06&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Elegant%20minimal%20abstract%20background%20soft%20cream%20and%20sage%20green%20tones%20subtle%20geometric%20lines%20sophisticated%20modern%20social%20media%20banner%20refined%20calm&width=1600&height=500&seq=fu-cover-oyente-06&orientation=landscape',
    city: 'Lima',
    country: 'Perú',
    countryFlag: '🇵🇪',
    bio: 'Oyente de radio desde que era niña. Me encantan los programas matutinos y los concursos de trivia. ¡Nunca fallo una!',
    level: 2,
    levelName: 'Activo',
    followersCount: 312,
    followingCount: 24,
    postsCount: 98,
    achievementsCount: 4,
    badges: ['Madrugadora'],
    isLive: false,
    isVerified: false,
    isFollowing: true,
    isBlocked: false,
    joinedDate: '2022-06-15',
    lastActive: 'Hace 3 horas',
    tags: ['Matutino', 'Trivia', 'Concursos'],
  },
  {
    id: 'oyente-7',
    type: 'oyente',
    name: 'Javier El Loco Ramírez',
    username: '@elloco_javier',
    photoUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20Latino%20man%20mid%20thirties%20fun%20energetic%20personality%20colorful%20shirt%20warm%20genuine%20laugh%20outdoor%20portrait%20natural%20daylight%20vibrant&width=400&height=400&seq=fu-oyente-07&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Festive%20colorful%20abstract%20background%20vibrant%20confetti%20and%20streamers%20celebratory%20red%20yellow%20and%20green%20tones%20energetic%20social%20media%20banner%20joyful%20fun&width=1600&height=500&seq=fu-cover-oyente-07&orientation=landscape',
    city: 'Santiago',
    country: 'Chile',
    countryFlag: '🇨🇱',
    bio: 'El más fiestero de la radio. Siempre pidiendo canciones y mandando saludos. Si hay concurso, ahí estoy. ¡Vamos Chile! 🇨🇱',
    level: 4,
    levelName: 'Top Fan',
    followersCount: 1567,
    followingCount: 67,
    postsCount: 421,
    achievementsCount: 11,
    badges: ['Campeón', 'Top Fan', 'VIP'],
    isLive: false,
    isVerified: true,
    isFollowing: true,
    isBlocked: false,
    joinedDate: '2019-09-01',
    lastActive: 'Ahora',
    tags: ['Fiestero', 'Concursos', 'Comunidad'],
  },
  {
    id: 'oyente-8',
    type: 'oyente',
    name: 'María José Vargas',
    username: '@majo_vargas',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20with%20glasses%20warm%20intellectual%20style%20soft%20smile%20creative%20casual%20outfit%20coffee%20shop%20background%20editorial%20portrait%20natural%20light&width=400&height=400&seq=fu-oyente-08&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Cozy%20book%20cafe%20abstract%20background%20warm%20brown%20and%20cream%20tones%20soft%20bokeh%20lights%20intellectual%20modern%20social%20media%20banner%20inviting%20calm&width=1600&height=500&seq=fu-cover-oyente-08&orientation=landscape',
    city: 'Quito',
    country: 'Ecuador',
    countryFlag: '🇪🇨',
    bio: 'Estudiante de periodismo. La radio es mi inspiración. Sueño con tener mi propio programa de entrevistas algún día.',
    level: 1,
    levelName: 'Nuevo Oyente',
    followersCount: 178,
    followingCount: 19,
    postsCount: 64,
    achievementsCount: 3,
    badges: ['Novata'],
    isLive: false,
    isVerified: false,
    isFollowing: false,
    isBlocked: false,
    joinedDate: '2023-02-18',
    lastActive: 'Hace 8 horas',
    tags: ['Periodismo', 'Entrevistas', 'Cultura'],
  },

  // ═══════ LOCUTORES (8) ═══════
  {
    id: 'locutor-1',
    type: 'locutor',
    name: 'Valentina Ríos',
    username: '@vale_radio',
    photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20radio%20host%20portrait%20confident%20warm%20smile%20studio%20with%20microphones%20golden%20hour%20lighting%20clean%20editorial%20photography%20charismatic&width=400&height=400&seq=fu-locutor-01&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Professional%20broadcast%20studio%20background%20warm%20ambient%20lighting%20microphones%20and%20mixing%20console%20elegant%20modern%20radio%20station%20atmosphere%20professional%20creative%20space&width=1600&height=500&seq=fu-cover-locutor-01&orientation=landscape',
    city: 'Buenos Aires',
    country: 'Argentina',
    countryFlag: '🇦🇷',
    bio: 'Locutora profesional. Conductora de Tardes con Vale. Amo conectar con mi audiencia y hacer que cada tarde sea especial.',
    level: 18,
    levelName: 'Locutora Estrella',
    followersCount: 31200,
    followingCount: 89,
    postsCount: 1240,
    achievementsCount: 24,
    badges: ['Locutora #1', 'Verificada', 'Embajadora RFW'],
    isLive: true,
    isVerified: true,
    isFollowing: true,
    isBlocked: false,
    joinedDate: '2018-04-10',
    lastActive: 'En cabina',
    tags: ['Talk Show', 'Carismática', 'Top'],
  },
  {
    id: 'locutor-2',
    type: 'locutor',
    name: 'Ricky Navarro',
    username: '@ricky_navarro',
    photoUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20Latino%20radio%20host%20portrait%20warm%20smile%20professional%20studio%20setting%20golden%20lighting%20clean%20background%20editorial%20photography%20friendly&width=400&height=400&seq=fu-locutor-02&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20Mexican%20themed%20broadcast%20studio%20background%20colorful%20papel%20picado%20warm%20fiesta%20atmosphere%20professional%20radio%20setup%20cultural%20celebratory&width=1600&height=500&seq=fu-cover-locutor-02&orientation=landscape',
    city: 'Ciudad de México',
    country: 'México',
    countryFlag: '🇲🇽',
    bio: 'El dueño de las mañanas en Banda Radio MX. Música regional, buena vibra y la mejor compañía para arrancar el día.',
    level: 17,
    levelName: 'Locutor Leyenda',
    followersCount: 28400,
    followingCount: 72,
    postsCount: 980,
    achievementsCount: 22,
    badges: ['Leyenda', 'Verificado', 'Embajador RFW'],
    isLive: true,
    isVerified: true,
    isFollowing: false,
    isBlocked: false,
    joinedDate: '2017-01-25',
    lastActive: 'En cabina',
    tags: ['Regional', 'Banda', 'Matutino'],
  },
  {
    id: 'locutor-3',
    type: 'locutor',
    name: 'Sofía Linares',
    username: '@sofia_dj',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20radio%20DJ%20portrait%20trendy%20street%20style%20urban%20studio%20with%20neon%20lights%20vibrant%20energetic%20atmosphere%20editorial%20lifestyle%20photography&width=400&height=400&seq=fu-locutor-03&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Urban%20neon%20lit%20broadcast%20studio%20pink%20and%20purple%20atmospheric%20lighting%20modern%20DJ%20setup%20vibrant%20night%20radio%20energy%20creative%20space&width=1600&height=500&seq=fu-cover-locutor-03&orientation=landscape',
    city: 'Medellín',
    country: 'Colombia',
    countryFlag: '🇨🇴',
    bio: 'DJ y locutora de La Kalle 99.9. Música urbana, reggaetón y la mejor energía. ¡Ponte los audífonos y súbele! 🔥',
    level: 15,
    levelName: 'Locutora Vibra',
    followersCount: 22100,
    followingCount: 105,
    postsCount: 1450,
    achievementsCount: 18,
    badges: ['DJ #1', 'Verificada', 'Top Creadora'],
    isLive: true,
    isVerified: true,
    isFollowing: true,
    isBlocked: false,
    joinedDate: '2019-06-12',
    lastActive: 'En cabina',
    tags: ['Urbano', 'Juvenil', 'DJ'],
  },
  {
    id: 'locutor-4',
    type: 'locutor',
    name: 'Carlos Mendoza',
    username: '@carlos_deportes',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20sports%20commentator%20portrait%20energetic%20expression%20professional%20broadcast%20studio%20dynamic%20lighting%20clean%20editorial%20photography%20passionate&width=400&height=400&seq=fu-locutor-04&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Sports%20broadcast%20studio%20background%20dynamic%20stadium%20atmosphere%20green%20field%20views%20multiple%20screens%20professional%20sports%20media%20setup%20energetic&width=1600&height=500&seq=fu-cover-locutor-04&orientation=landscape',
    city: 'Monterrey',
    country: 'México',
    countryFlag: '🇲🇽',
    bio: 'Comentarista deportivo. Conductor de Deporte Total. El fútbol se vive, se sufre, se grita... y se transmite.',
    level: 16,
    levelName: 'Locutor Campeón',
    followersCount: 19800,
    followingCount: 56,
    postsCount: 870,
    achievementsCount: 17,
    badges: ['Comentarista Top', 'Verificado', 'Ganador'],
    isLive: false,
    isVerified: true,
    isFollowing: false,
    isBlocked: false,
    joinedDate: '2018-08-30',
    lastActive: 'Hace 2 horas',
    tags: ['Deportes', 'Comentarista', 'Fútbol'],
  },
  {
    id: 'locutor-5',
    type: 'locutor',
    name: 'Diego Montenegro',
    username: '@dieguito_rock',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20comedian%20radio%20host%20portrait%20humorous%20expression%20casual%20relaxed%20studio%20vibe%20warm%20atmospheric%20lighting%20authentic%20personality%20rock%20style&width=400&height=400&seq=fu-locutor-05&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Rock%20music%20broadcast%20studio%20background%20electric%20guitars%20on%20wall%20dark%20moody%20lighting%20vintage%20amplifiers%20edgy%20creative%20atmosphere%20authentic%20rock%20vibe&width=1600&height=500&seq=fu-cover-locutor-05&orientation=landscape',
    city: 'Buenos Aires',
    country: 'Argentina',
    countryFlag: '🇦🇷',
    bio: 'Comediante y rockero. Conductor de Onda Nocturna. Si no te reíste, no fue mi programa. Rock y humor, la combinación perfecta.',
    level: 14,
    levelName: 'Locutor Rockstar',
    followersCount: 16500,
    followingCount: 48,
    postsCount: 720,
    achievementsCount: 14,
    badges: ['Comediante Top', 'Verificado'],
    isLive: false,
    isVerified: true,
    isFollowing: true,
    isBlocked: false,
    joinedDate: '2019-02-14',
    lastActive: 'Hace 5 horas',
    tags: ['Comedia', 'Rock', 'Nocturno'],
  },
  {
    id: 'locutor-6',
    type: 'locutor',
    name: 'Alejandro Ramírez',
    username: '@ale_locutor',
    photoUrl: 'https://readdy.ai/api/search-image?query=Mature%20distinguished%20Latino%20radio%20host%20portrait%20silver%20hair%20warm%20smile%20professional%20studio%20setting%20authoritative%20dignified%20experienced%20broadcast%20veteran&width=400&height=400&seq=fu-locutor-06&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Classic%20elegant%20broadcast%20studio%20background%20warm%20wood%20tones%20vintage%20microphones%20timeless%20radio%20atmosphere%20sophisticated%20professional%20experienced&width=1600&height=500&seq=fu-cover-locutor-06&orientation=landscape',
    city: 'Ciudad de México',
    country: 'México',
    countryFlag: '🇲🇽',
    bio: 'Más de 20 años frente al micrófono. La radio es mi vida. Conductor de El Sabor del Mediodía en La Mexicana FM.',
    level: 20,
    levelName: 'Maestro de la Radio',
    followersCount: 23400,
    followingCount: 34,
    postsCount: 560,
    achievementsCount: 28,
    badges: ['Maestro', 'Leyenda', 'Verificado', 'Embajador RFW'],
    isLive: false,
    isVerified: true,
    isFollowing: false,
    isBlocked: false,
    joinedDate: '2005-03-01',
    lastActive: 'Hace 6 horas',
    tags: ['Regional', 'Veterano', 'Maestro'],
  },
  {
    id: 'locutor-7',
    type: 'locutor',
    name: 'Daniela La Voz Urbina',
    username: '@daniela_urbina',
    photoUrl: 'https://readdy.ai/api/search-image?query=Friendly%20Latina%20female%20radio%20host%20bright%20engaging%20smile%20professional%20broadcast%20headset%20modern%20studio%20warm%20lighting%20authentic%20editorial%20portrait&width=400&height=400&seq=fu-locutor-07&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Bright%20modern%20radio%20studio%20background%20fresh%20morning%20light%20plants%20natural%20elements%20clean%20professional%20broadcast%20space%20inviting%20positive%20energy&width=1600&height=500&seq=fu-cover-locutor-07&orientation=landscape',
    city: 'Caracas',
    country: 'Venezuela',
    countryFlag: '🇻🇪',
    bio: 'Locutora venezolana con acento y sabor caribeño. La música y la buena conversación son mi fórmula mágica. ¡Bienvenidos a mi cabina!',
    level: 13,
    levelName: 'Locutora Inspiración',
    followersCount: 18700,
    followingCount: 61,
    postsCount: 650,
    achievementsCount: 15,
    badges: ['Verificada', 'Inspiración'],
    isLive: false,
    isVerified: true,
    isFollowing: false,
    isBlocked: false,
    joinedDate: '2020-01-30',
    lastActive: 'Hace 12 horas',
    tags: ['Caribe', 'Conversación', 'Música'],
  },
  {
    id: 'locutor-8',
    type: 'locutor',
    name: 'Roberto Aguilar',
    username: '@beto_aguilar',
    photoUrl: 'https://readdy.ai/api/search-image?query=Mature%20distinguished%20Latino%20radio%20host%20warm%20smile%20salt%20and%20pepper%20hair%20classic%20professional%20style%20clean%20portrait%20experienced%20authoritative%20friendly&width=400&height=400&seq=fu-locutor-08&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Traditional%20Mexican%20cultural%20broadcast%20studio%20warm%20earthy%20tones%20folk%20art%20decorations%20classic%20radio%20atmosphere%20heritage%20authentic%20professional&width=1600&height=500&seq=fu-cover-locutor-08&orientation=landscape',
    city: 'Guadalajara',
    country: 'México',
    countryFlag: '🇲🇽',
    bio: 'Locutor de la vieja escuela. Amo la cultura mexicana y compartirla al aire. La Mexicana FM es tradición y corazón.',
    level: 19,
    levelName: 'Locutor Tradición',
    followersCount: 14200,
    followingCount: 28,
    postsCount: 390,
    achievementsCount: 20,
    badges: ['Tradición', 'Verificado', 'Leyenda'],
    isLive: false,
    isVerified: true,
    isFollowing: true,
    isBlocked: false,
    joinedDate: '2010-05-15',
    lastActive: 'Hace 4 horas',
    tags: ['Regional', 'Cultura', 'Tradición'],
  },

  // ═══════ MODERADORES (6) ═══════
  {
    id: 'mod-1',
    type: 'moderador',
    name: 'Fernanda Castro',
    username: '@fernanda_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20late%20twenties%20friendly%20but%20authoritative%20style%20clean%20studio%20portrait%20warm%20but%20professional%20lighting%20editorial%20photography&width=400&height=400&seq=fu-mod-01&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Professional%20community%20management%20workspace%20abstract%20background%20organized%20clean%20blue%20teal%20and%20warm%20gray%20tones%20modern%20professional%20social%20media%20banner&width=1600&height=500&seq=fu-cover-mod-01&orientation=landscape',
    city: 'Ciudad de México',
    country: 'México',
    countryFlag: '🇲🇽',
    bio: 'Moderadora principal de RFW. Mantengo la comunidad segura y respetuosa. Si hay algún problema, aquí estoy para ayudar.',
    level: 22,
    levelName: 'Guardiana de la Comunidad',
    followersCount: 5400,
    followingCount: 120,
    postsCount: 340,
    achievementsCount: 16,
    badges: ['Moderadora Elite', 'Verificada', 'Guardiana'],
    isLive: false,
    isVerified: true,
    isFollowing: true,
    isBlocked: false,
    joinedDate: '2019-05-20',
    lastActive: 'Ahora',
    tags: ['Comunidad', 'Seguridad', 'Soporte'],
  },
  {
    id: 'mod-2',
    type: 'moderador',
    name: 'Miguel Ángel Torres',
    username: '@miguel_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20thirties%20calm%20professional%20demeanor%20clean%20cut%20style%20friendly%20reassuring%20smile%20studio%20portrait%20warm%20lighting%20authoritative%20approachable&width=400&height=400&seq=fu-mod-02&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Calm%20organized%20workspace%20abstract%20background%20cool%20gray%20and%20soft%20teal%20tones%20clean%20lines%20orderly%20modern%20professional%20social%20media%20banner&width=1600&height=500&seq=fu-cover-mod-02&orientation=landscape',
    city: 'Buenos Aires',
    country: 'Argentina',
    countryFlag: '🇦🇷',
    bio: 'Moderador nocturno. Cuido la comunidad mientras todos duermen. La radio nunca para y la moderación tampoco.',
    level: 19,
    levelName: 'Moderador Nocturno',
    followersCount: 3200,
    followingCount: 85,
    postsCount: 210,
    achievementsCount: 12,
    badges: ['Moderador Elite', 'Verificado', 'Nocturno'],
    isLive: false,
    isVerified: true,
    isFollowing: false,
    isBlocked: false,
    joinedDate: '2020-08-15',
    lastActive: 'Hace 3 horas',
    tags: ['Nocturno', 'Comunidad', 'Dedicación'],
  },
  {
    id: 'mod-3',
    type: 'moderador',
    name: 'Andrea Suárez',
    username: '@andrea_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20professional%20Latina%20woman%20warm%20smile%20glasses%20sharp%20intelligent%20look%20clean%20modern%20studio%20portrait%20friendly%20approachable%20editorial%20style&width=400&height=400&seq=fu-mod-03&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Organized%20creative%20workspace%20abstract%20background%20warm%20white%20and%20soft%20coral%20tones%20tidy%20minimal%20modern%20professional%20social%20media%20banner&width=1600&height=500&seq=fu-cover-mod-03&orientation=landscape',
    city: 'Lima',
    country: 'Perú',
    countryFlag: '🇵🇪',
    bio: 'Moderadora de RFW Perú. Especialista en resolución de conflictos y en hacer que todos se sientan bienvenidos.',
    level: 17,
    levelName: 'Moderadora Pacífica',
    followersCount: 2800,
    followingCount: 65,
    postsCount: 190,
    achievementsCount: 10,
    badges: ['Moderadora', 'Verificada', 'Pacifista'],
    isLive: false,
    isVerified: true,
    isFollowing: true,
    isBlocked: false,
    joinedDate: '2021-02-28',
    lastActive: 'Hace 1 hora',
    tags: ['Resolución', 'Bienvenida', 'Soporte'],
  },
  {
    id: 'mod-4',
    type: 'moderador',
    name: 'Pedro El Guardián Díaz',
    username: '@pedro_guardian',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20forties%20strong%20but%20kind%20expression%20security%20professional%20style%20clean%20portrait%20authoritative%20warm%20natural%20lighting&width=400&height=400&seq=fu-mod-04&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Secure%20professional%20workspace%20abstract%20background%20deep%20navy%20and%20silver%20tones%20shield%20motif%20subtle%20modern%20cyber%20security%20social%20media%20banner&width=1600&height=500&seq=fu-cover-mod-04&orientation=landscape',
    city: 'Santiago',
    country: 'Chile',
    countryFlag: '🇨🇱',
    bio: 'Moderador Senior. 5 años protegiendo la comunidad RFW. Tolerancia cero al hate, bienvenida total al respeto.',
    level: 24,
    levelName: 'Guardián Supremo',
    followersCount: 4100,
    followingCount: 42,
    postsCount: 280,
    achievementsCount: 18,
    badges: ['Guardián Supremo', 'Verificado', 'Veterano', 'Senior'],
    isLive: false,
    isVerified: true,
    isFollowing: false,
    isBlocked: false,
    joinedDate: '2018-12-01',
    lastActive: 'Hace 30 min',
    tags: ['Senior', 'Protección', 'Veterano'],
  },
  {
    id: 'mod-5',
    type: 'moderador',
    name: 'Carolina Méndez',
    username: '@caro_mod',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20warm%20genuine%20smile%20casual%20friendly%20style%20approachable%20soft%20natural%20lighting%20editorial%20portrait%20authentic&width=400&height=400&seq=fu-mod-05&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Warm%20community%20gathering%20abstract%20background%20soft%20yellow%20and%20peach%20tones%20friendly%20connected%20dots%20pattern%20welcoming%20modern%20social%20media%20banner&width=1600&height=500&seq=fu-cover-mod-05&orientation=landscape',
    city: 'Medellín',
    country: 'Colombia',
    countryFlag: '🇨🇴',
    bio: 'Moderadora de La Kalle 99.9. La energía de la comunidad urbana es increíble. Mi trabajo es que fluya con respeto.',
    level: 15,
    levelName: 'Moderadora Vibra',
    followersCount: 2100,
    followingCount: 78,
    postsCount: 160,
    achievementsCount: 8,
    badges: ['Moderadora', 'Verificada'],
    isLive: false,
    isVerified: true,
    isFollowing: false,
    isBlocked: false,
    joinedDate: '2021-09-10',
    lastActive: 'Hace 2 horas',
    tags: ['Urbano', 'Comunidad', 'Energía'],
  },
  {
    id: 'mod-6',
    type: 'moderador',
    name: 'Luis Content Creator',
    username: '@luis_content',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20creative%20style%20casual%20modern%20fashion%20content%20creator%20vibe%20warm%20studio%20lighting%20portrait%20artistic%20editorial&width=400&height=400&seq=fu-mod-06&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Creative%20content%20studio%20abstract%20background%20vibrant%20coral%20and%20teal%20artistic%20tools%20modern%20creative%20professional%20social%20media%20banner%20imaginative&width=1600&height=500&seq=fu-cover-mod-06&orientation=landscape',
    city: 'Ciudad de México',
    country: 'México',
    countryFlag: '🇲🇽',
    bio: 'Moderador de contenido. Reviso posts, fotos y videos para mantener la calidad. Si tu contenido es bueno, yo lo apruebo.',
    level: 16,
    levelName: 'Curador de Contenido',
    followersCount: 1800,
    followingCount: 55,
    postsCount: 130,
    achievementsCount: 9,
    badges: ['Curador', 'Verificado'],
    isLive: false,
    isVerified: true,
    isFollowing: true,
    isBlocked: false,
    joinedDate: '2022-01-10',
    lastActive: 'Hace 4 horas',
    tags: ['Contenido', 'Calidad', 'Curación'],
  },

  // ═══════ CREADORES (6) ═══════
  {
    id: 'creador-1',
    type: 'creador',
    name: 'Juan Pablo Estudio',
    username: '@juanpablo_crea',
    photoUrl: 'https://readdy.ai/api/search-image?query=Creative%20Latino%20man%20thirties%20artistic%20style%20with%20headphones%20in%20modern%20studio%20setting%20music%20production%20vibe%20warm%20professional%20lighting%20editorial%20portrait&width=400&height=400&seq=fu-creador-01&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Professional%20music%20production%20studio%20background%20audio%20equipment%20mixing%20console%20speakers%20creative%20workspace%20atmospheric%20lighting%20modern%20creative&width=1600&height=500&seq=fu-cover-creador-01&orientation=landscape',
    city: 'Ciudad de México',
    country: 'México',
    countryFlag: '🇲🇽',
    bio: 'Productor de contenido para radio. Jingles, podcasts, spots y todo lo que suene bien. Si lo imaginás, lo creamos juntos.',
    level: 14,
    levelName: 'Creador Pro',
    followersCount: 8900,
    followingCount: 34,
    postsCount: 520,
    achievementsCount: 14,
    badges: ['Creador Pro', 'Verificado', 'Top Productor'],
    isLive: false,
    isVerified: true,
    isFollowing: true,
    isBlocked: false,
    joinedDate: '2019-10-05',
    lastActive: 'Hace 1 hora',
    tags: ['Producción', 'Jingles', 'Podcast'],
  },
  {
    id: 'creador-2',
    type: 'creador',
    name: 'Camila Artista Digital',
    username: '@cami_digital',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20digital%20artist%20woman%20colorful%20creative%20style%20graphic%20tablet%20modern%20studio%20artistic%20vibrant%20personality%20editorial%20portrait%20warm%20lighting&width=400&height=400&seq=fu-creador-02&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Digital%20art%20studio%20background%20colorful%20illustrations%20graphic%20tablet%20creative%20tools%20vibrant%20artistic%20atmosphere%20modern%20creative%20workspace&width=1600&height=500&seq=fu-cover-creador-02&orientation=landscape',
    city: 'Buenos Aires',
    country: 'Argentina',
    countryFlag: '🇦🇷',
    bio: 'Diseñadora gráfica especializada en branding para radios. Le doy identidad visual a las estaciones que amás.',
    level: 12,
    levelName: 'Artista Digital',
    followersCount: 7200,
    followingCount: 42,
    postsCount: 380,
    achievementsCount: 10,
    badges: ['Artista Top', 'Verificada', 'Creativa'],
    isLive: false,
    isVerified: true,
    isFollowing: false,
    isBlocked: false,
    joinedDate: '2020-04-18',
    lastActive: 'Hace 5 horas',
    tags: ['Diseño', 'Branding', 'Visual'],
  },
  {
    id: 'creador-3',
    type: 'creador',
    name: 'Felipe El Podcaster',
    username: '@felipe_podcast',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20podcast%20creator%20style%20casual%20trendy%20headphones%20around%20neck%20modern%20studio%20background%20warm%20lighting%20editorial%20portrait%20creative&width=400&height=400&seq=fu-creador-03&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Podcast%20recording%20studio%20background%20professional%20microphones%20acoustic%20panels%20warm%20ambient%20lighting%20creative%20audio%20workspace%20modern&width=1600&height=500&seq=fu-cover-creador-03&orientation=landscape',
    city: 'Medellín',
    country: 'Colombia',
    countryFlag: '🇨🇴',
    bio: 'Creador de Historias que Suenan, el podcast más escuchado de RFW. Entrevistas, historias y el detrás de escena de la radio.',
    level: 13,
    levelName: 'Podcaster Estrella',
    followersCount: 10400,
    followingCount: 28,
    postsCount: 290,
    achievementsCount: 12,
    badges: ['Podcaster #1', 'Verificado', 'Top Creador'],
    isLive: false,
    isVerified: true,
    isFollowing: true,
    isBlocked: false,
    joinedDate: '2020-07-15',
    lastActive: 'Hace 2 horas',
    tags: ['Podcast', 'Entrevistas', 'Historias'],
  },
  {
    id: 'creador-4',
    type: 'creador',
    name: 'Natalia Videógrafa',
    username: '@nati_video',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20videographer%20woman%20with%20professional%20camera%20creative%20field%20style%20warm%20natural%20lighting%20outdoor%20editorial%20portrait%20artistic%20documentary&width=400&height=400&seq=fu-creador-04&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Video%20production%20studio%20background%20professional%20cameras%20lighting%20equipment%20creative%20workspace%20cinematic%20atmosphere%20modern%20film%20studio&width=1600&height=500&seq=fu-cover-creador-04&orientation=landscape',
    city: 'Lima',
    country: 'Perú',
    countryFlag: '🇵🇪',
    bio: 'Videógrafa y editora. Creo contenido visual para radios que quieren brillar. Del vivo a la pantalla, yo lo hago posible.',
    level: 11,
    levelName: 'Cineasta Radial',
    followersCount: 5600,
    followingCount: 38,
    postsCount: 240,
    achievementsCount: 8,
    badges: ['Videógrafa Top', 'Verificada'],
    isLive: false,
    isVerified: true,
    isFollowing: false,
    isBlocked: false,
    joinedDate: '2021-03-22',
    lastActive: 'Hace 6 horas',
    tags: ['Video', 'Edición', 'Visual'],
  },
  {
    id: 'creador-5',
    type: 'creador',
    name: 'Ricardo El Escritor',
    username: '@ricky_escribe',
    photoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20thirties%20writer%20intellectual%20style%20glasses%20creative%20thoughtful%20expression%20coffee%20shop%20background%20warm%20lighting%20editorial%20portrait&width=400&height=400&seq=fu-creador-05&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Writer%20creative%20workspace%20background%20books%20typewriter%20warm%20cozy%20lighting%20intellectual%20atmosphere%20vintage%20modern%20creative%20studio&width=1600&height=500&seq=fu-cover-creador-05&orientation=landscape',
    city: 'Buenos Aires',
    country: 'Argentina',
    countryFlag: '🇦🇷',
    bio: 'Escritor y guionista para radio. Creo los libretos que hacen brillar a los locutores. Las palabras son mi herramienta.',
    level: 10,
    levelName: 'Escritor Creativo',
    followersCount: 3400,
    followingCount: 22,
    postsCount: 180,
    achievementsCount: 7,
    badges: ['Escritor Top', 'Verificado'],
    isLive: false,
    isVerified: true,
    isFollowing: true,
    isBlocked: false,
    joinedDate: '2021-08-05',
    lastActive: 'Hace 3 horas',
    tags: ['Guiones', 'Escritura', 'Creatividad'],
  },
  {
    id: 'creador-6',
    type: 'creador',
    name: 'Gabriela Sonidista',
    username: '@gabi_sonido',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20sound%20engineer%20style%20professional%20headphones%20in%20recording%20studio%20technical%20creative%20vibe%20warm%20ambient%20lighting%20editorial%20portrait&width=400&height=400&seq=fu-creador-06&orientation=squarish',
    coverUrl: 'https://readdy.ai/api/search-image?query=Professional%20sound%20engineering%20studio%20background%20mixing%20console%20audio%20waveforms%20monitors%20technical%20creative%20workspace%20atmospheric&width=1600&height=500&seq=fu-cover-creador-06&orientation=landscape',
    city: 'Santiago',
    country: 'Chile',
    countryFlag: '🇨🇱',
    bio: 'Ingeniera de sonido. Mezclo, masterizo y hago que cada transmisión suene impecable. El audio es mi pasión.',
    level: 13,
    levelName: 'Ingeniera de Audio',
    followersCount: 4800,
    followingCount: 30,
    postsCount: 210,
    achievementsCount: 9,
    badges: ['Sonidista Pro', 'Verificada', 'Técnica'],
    isLive: false,
    isVerified: true,
    isFollowing: false,
    isBlocked: false,
    joinedDate: '2020-11-12',
    lastActive: 'Hace 8 horas',
    tags: ['Audio', 'Sonido', 'Técnica'],
  },
];

export const followActivity: FollowActivityItem[] = [
  { id: 1, userId: 'locutor-3', userName: 'Sofía Linares', userPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latina%20radio%20DJ%20portrait%20trendy%20street%20style%20urban%20studio%20with%20neon%20lights%20vibrant%20energetic%20atmosphere%20editorial%20lifestyle%20photography&width=80&height=80&seq=fa-01&orientation=squarish', type: 'post', description: 'Publicó un nuevo video: Backstage de La Kalle esta noche 🔥', icon: 'ri-video-line', iconColor: 'text-rose-600', iconBg: 'bg-rose-50', timestamp: '2026-06-21T02:45:00Z', timestampRelative: 'Hace 10 min' },
  { id: 2, userId: 'oyente-1', userName: 'Marco Antonio Rivera', userPhoto: 'https://readdy.ai/api/search-image?query=Young%20confident%20Latino%20male%20portrait%20warm%20genuine%20friendly%20smile%20casual%20olive%20green%20henley%20shirt%20soft%20warm%20cream%20background%20natural%20lighting%20approachable%20authentic%20headshot&width=80&height=80&seq=fa-02&orientation=squarish', type: 'seguir', description: 'Empezó a seguir a Valentina Ríos', icon: 'ri-user-add-line', iconColor: 'text-emerald-600', iconBg: 'bg-emerald-50', timestamp: '2026-06-21T02:30:00Z', timestampRelative: 'Hace 25 min' },
  { id: 3, userId: 'locutor-1', userName: 'Valentina Ríos', userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20radio%20host%20portrait%20confident%20warm%20smile%20studio%20with%20microphones%20golden%20hour%20lighting%20clean%20editorial%20photography%20charismatic&width=80&height=80&seq=fa-03&orientation=squarish', type: 'logro', description: '¡Alcanzó los 31,200 seguidores! Nuevo hito desbloqueado 🏆', icon: 'ri-medal-line', iconColor: 'text-amber-600', iconBg: 'bg-amber-50', timestamp: '2026-06-21T02:00:00Z', timestampRelative: 'Hace 55 min' },
  { id: 4, userId: 'creador-3', userName: 'Felipe El Podcaster', userPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20podcast%20creator%20style%20casual%20trendy%20headphones%20around%20neck%20modern%20studio%20background%20warm%20lighting%20editorial%20portrait%20creative&width=80&height=80&seq=fa-04&orientation=squarish', type: 'post', description: 'Nuevo episodio: Entrevista exclusiva con Diego Montenegro sobre comedia y radio', icon: 'ri-mic-line', iconColor: 'text-violet-600', iconBg: 'bg-violet-50', timestamp: '2026-06-21T01:30:00Z', timestampRelative: 'Hace 1.5 horas' },
  { id: 5, userId: 'mod-1', userName: 'Fernanda Castro', userPhoto: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20late%20twenties%20friendly%20but%20authoritative%20style%20clean%20studio%20portrait%20warm%20but%20professional%20lighting%20editorial%20photography&width=80&height=80&seq=fa-05&orientation=squarish', type: 'post', description: 'Recordatorio: Las reglas de convivencia se actualizaron. ¡Léelas y mantengamos la buena vibra!', icon: 'ri-article-line', iconColor: 'text-sky-600', iconBg: 'bg-sky-50', timestamp: '2026-06-21T01:00:00Z', timestampRelative: 'Hace 2 horas' },
  { id: 6, userId: 'oyente-7', userName: 'Javier El Loco Ramírez', userPhoto: 'https://readdy.ai/api/search-image?query=Charismatic%20Latino%20man%20mid%20thirties%20fun%20energetic%20personality%20colorful%20shirt%20warm%20genuine%20laugh%20outdoor%20portrait%20natural%20daylight%20vibrant&width=80&height=80&seq=fa-06&orientation=squarish', type: 'seguir', description: 'Empezó a seguir a 3 nuevos oyentes', icon: 'ri-user-add-line', iconColor: 'text-emerald-600', iconBg: 'bg-emerald-50', timestamp: '2026-06-20T23:45:00Z', timestampRelative: 'Hace 3 horas' },
  { id: 7, userId: 'locutor-2', userName: 'Ricky Navarro', userPhoto: 'https://readdy.ai/api/search-image?query=Charismatic%20Latino%20radio%20host%20portrait%20warm%20smile%20professional%20studio%20setting%20golden%20lighting%20clean%20background%20editorial%20photography%20friendly&width=80&height=80&seq=fa-07&orientation=squarish', type: 'logro', description: 'Obtuvo la insignia Leyenda por más de 28K seguidores leales', icon: 'ri-vip-diamond-line', iconColor: 'text-sky-600', iconBg: 'bg-sky-50', timestamp: '2026-06-20T22:00:00Z', timestampRelative: 'Hace 5 horas' },
  { id: 8, userId: 'creador-2', userName: 'Camila Artista Digital', userPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latina%20digital%20artist%20woman%20colorful%20creative%20style%20graphic%20tablet%20modern%20studio%20artistic%20vibrant%20personality%20editorial%20portrait%20warm%20lighting&width=80&height=80&seq=fa-08&orientation=squarish', type: 'post', description: 'Compartió el nuevo rediseño de logo para Radio Peninsular — ¡quedó espectacular!', icon: 'ri-image-line', iconColor: 'text-orange-600', iconBg: 'bg-orange-50', timestamp: '2026-06-20T21:00:00Z', timestampRelative: 'Hace 6 horas' },
  { id: 9, userId: 'oyente-2', userName: 'Sofía Hernández', userPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20portrait%20friendly%20warm%20smile%20casual%20modern%20style%20soft%20warm%20neutral%20background%20editorial%20headshot%20clean%20professional%20photography&width=80&height=80&seq=fa-09&orientation=squarish', type: 'seguir', description: 'Empezó a seguir a Carlos Mendoza y a Diego Montenegro', icon: 'ri-user-add-line', iconColor: 'text-emerald-600', iconBg: 'bg-emerald-50', timestamp: '2026-06-20T20:30:00Z', timestampRelative: 'Hace 7 horas' },
  { id: 10, userId: 'locutor-5', userName: 'Diego Montenegro', userPhoto: 'https://readdy.ai/api/search-image?query=Latino%20comedian%20radio%20host%20portrait%20humorous%20expression%20casual%20relaxed%20studio%20vibe%20warm%20atmospheric%20lighting%20authentic%20personality%20rock%20style&width=80&height=80&seq=fa-10&orientation=squarish', type: 'post', description: 'Compartió un clip de lo mejor de Onda Nocturna — ¡no paro de reír! 😂', icon: 'ri-emotion-laugh-line', iconColor: 'text-amber-600', iconBg: 'bg-amber-50', timestamp: '2026-06-20T20:00:00Z', timestampRelative: 'Hace 7 horas' },
  { id: 11, userId: 'oyente-5', userName: 'Diego Martínez', userPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20early%20twenties%20trendy%20streetwear%20style%20confident%20expression%20urban%20background%20portrait%20photography%20casual%20cool%20modern&width=80&height=80&seq=fa-11&orientation=squarish', type: 'logro', description: 'Alcanzó los 1,000 seguidores — ¡bienvenido al club de los mil!', icon: 'ri-star-line', iconColor: 'text-yellow-600', iconBg: 'bg-yellow-50', timestamp: '2026-06-20T19:00:00Z', timestampRelative: 'Hace 8 horas' },
  { id: 12, userId: 'creador-5', userName: 'Ricardo El Escritor', userPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20thirties%20writer%20intellectual%20style%20glasses%20creative%20thoughtful%20expression%20coffee%20shop%20background%20warm%20lighting%20editorial%20portrait&width=80&height=80&seq=fa-12&orientation=squarish', type: 'post', description: 'Publicó una guía sobre cómo escribir libretos de radio que conectan', icon: 'ri-file-text-line', iconColor: 'text-indigo-600', iconBg: 'bg-indigo-50', timestamp: '2026-06-20T18:00:00Z', timestampRelative: 'Hace 9 horas' },
  { id: 13, userId: 'locutor-4', userName: 'Carlos Mendoza', userPhoto: 'https://readdy.ai/api/search-image?query=Latino%20sports%20commentator%20portrait%20energetic%20expression%20professional%20broadcast%20studio%20dynamic%20lighting%20clean%20editorial%20photography%20passionate&width=80&height=80&seq=fa-13&orientation=squarish', type: 'post', description: 'Análisis post-partido: lo que dejó la final de la Copa — imperdible', icon: 'ri-football-line', iconColor: 'text-emerald-600', iconBg: 'bg-emerald-50', timestamp: '2026-06-20T17:00:00Z', timestampRelative: 'Hace 10 horas' },
  { id: 14, userId: 'oyente-4', userName: 'Lucía Fernández', userPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20curly%20dark%20hair%20warm%20genuine%20smile%20artistic%20bohemian%20style%20soft%20golden%20hour%20lighting%20editorial%20portrait%20gentle%20natural%20authentic&width=80&height=80&seq=fa-14&orientation=squarish', type: 'seguir', description: 'Empezó a seguir a Juan Pablo Estudio y a Camila Artista Digital', icon: 'ri-user-add-line', iconColor: 'text-emerald-600', iconBg: 'bg-emerald-50', timestamp: '2026-06-20T16:00:00Z', timestampRelative: 'Hace 11 horas' },
  { id: 15, userId: 'mod-4', userName: 'Pedro El Guardián Díaz', userPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20forties%20strong%20but%20kind%20expression%20security%20professional%20style%20clean%20portrait%20authoritative%20warm%20natural%20lighting&width=80&height=80&seq=fa-15&orientation=squarish', type: 'logro', description: '5 años como Guardián de la Comunidad RFW — ¡gracias por tu servicio!', icon: 'ri-shield-check-line', iconColor: 'text-rose-600', iconBg: 'bg-rose-50', timestamp: '2026-06-20T15:00:00Z', timestampRelative: 'Hace 12 horas' },
];

export const followNotificationsList: FollowNotification[] = [
  {
    id: 1,
    type: 'nuevo-seguidor',
    fromUserId: 'oyente-2',
    fromName: 'Sofía Hernández',
    fromPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20portrait%20friendly%20warm%20smile%20casual%20modern%20style%20soft%20warm%20neutral%20background%20editorial%20headshot%20clean%20professional%20photography&width=80&height=80&seq=fn-01&orientation=squarish',
    description: 'Empezó a seguirte. ¡Saludala!',
    icon: 'ri-user-add-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    timestamp: 'Hace 15 min',
    isRead: false,
    actionUrl: '/seguir-usuarios',
  },
  {
    id: 2,
    type: 'nuevo-logro',
    fromUserId: 'oyente-1',
    fromName: 'Marco Antonio Rivera',
    fromPhoto: 'https://readdy.ai/api/search-image?query=Young%20confident%20Latino%20male%20portrait%20warm%20genuine%20friendly%20smile%20casual%20olive%20green%20henley%20shirt%20soft%20warm%20cream%20background%20natural%20lighting%20approachable%20authentic%20headshot&width=80&height=80&seq=fn-02&orientation=squarish',
    description: 'Alcanzó el nivel 4 — Top Fan. ¡Felicitalo!',
    icon: 'ri-medal-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    timestamp: 'Hace 1 hora',
    isRead: false,
    actionUrl: '/seguir-usuarios',
  },
  {
    id: 3,
    type: 'nuevo-post',
    fromUserId: 'locutor-1',
    fromName: 'Valentina Ríos',
    fromPhoto: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20radio%20host%20portrait%20confident%20warm%20smile%20studio%20with%20microphones%20golden%20hour%20lighting%20clean%20editorial%20photography%20charismatic&width=80&height=80&seq=fn-03&orientation=squarish',
    description: 'Publicó: Hoy en Tardes con Vale tenemos invitado sorpresa. ¿Quién será? 🤔',
    icon: 'ri-article-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    timestamp: 'Hace 2 horas',
    isRead: false,
    actionUrl: '/seguir-usuarios',
  },
  {
    id: 4,
    type: 'nuevo-seguidor',
    fromUserId: 'oyente-3',
    fromName: 'Carlos Mendoza',
    fromPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20mid%20twenties%20casual%20style%20warm%20smile%20premium%20portrait%20photography%20soft%20neutral%20background%20editorial%20quality%20headshot%20approachable&width=80&height=80&seq=fn-04&orientation=squarish',
    description: 'Empezó a seguirte. Tienen 3 amigos en común.',
    icon: 'ri-user-add-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    timestamp: 'Hace 3 horas',
    isRead: true,
    actionUrl: '/seguir-usuarios',
  },
  {
    id: 5,
    type: 'nuevo-post',
    fromUserId: 'creador-3',
    fromName: 'Felipe El Podcaster',
    fromPhoto: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20podcast%20creator%20style%20casual%20trendy%20headphones%20around%20neck%20modern%20studio%20background%20warm%20lighting%20editorial%20portrait%20creative&width=80&height=80&seq=fn-05&orientation=squarish',
    description: 'Nuevo episodio del podcast con invitado especial. ¡No te lo pierdas!',
    icon: 'ri-mic-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    timestamp: 'Hace 5 horas',
    isRead: true,
    actionUrl: '/seguir-usuarios',
  },
  {
    id: 6,
    type: 'nuevo-logro',
    fromUserId: 'oyente-7',
    fromName: 'Javier El Loco Ramírez',
    fromPhoto: 'https://readdy.ai/api/search-image?query=Charismatic%20Latino%20man%20mid%20thirties%20fun%20energetic%20personality%20colorful%20shirt%20warm%20genuine%20laugh%20outdoor%20portrait%20natural%20daylight%20vibrant&width=80&height=80&seq=fn-06&orientation=squarish',
    description: '¡Llegó a 1,500 seguidores! Un loco muy querido.',
    icon: 'ri-medal-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    timestamp: 'Hace 8 horas',
    isRead: true,
    actionUrl: '/seguir-usuarios',
  },
  {
    id: 7,
    type: 'nuevo-seguidor',
    fromUserId: 'oyente-6',
    fromName: 'Ana Paula Torres',
    fromPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20professional%20elegant%20style%20warm%20smile%20pearl%20earrings%20soft%20studio%20lighting%20editorial%20portrait%20refined%20sophisticated%20approachable&width=80&height=80&seq=fn-07&orientation=squarish',
    description: 'Empezó a seguirte. También sigue a 2 personas que vos seguís.',
    icon: 'ri-user-add-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    timestamp: 'Ayer',
    isRead: true,
    actionUrl: '/seguir-usuarios',
  },
  {
    id: 8,
    type: 'nuevo-post',
    fromUserId: 'mod-1',
    fromName: 'Fernanda Castro',
    fromPhoto: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20late%20twenties%20friendly%20but%20authoritative%20style%20clean%20studio%20portrait%20warm%20but%20professional%20lighting%20editorial%20photography&width=80&height=80&seq=fn-08&orientation=squarish',
    description: 'Tips para mantener una comunidad saludable — Parte 3.',
    icon: 'ri-article-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    timestamp: 'Ayer',
    isRead: true,
    actionUrl: '/seguir-usuarios',
  },
  {
    id: 9,
    type: 'nuevo-logro',
    fromUserId: 'creador-1',
    fromName: 'Juan Pablo Estudio',
    fromPhoto: 'https://readdy.ai/api/search-image?query=Creative%20Latino%20man%20thirties%20artistic%20style%20with%20headphones%20in%20modern%20studio%20setting%20music%20production%20vibe%20warm%20professional%20lighting%20editorial%20portrait&width=80&height=80&seq=fn-09&orientation=squarish',
    description: 'Alcanzó los 100 jingles producidos para radios RFW.',
    icon: 'ri-medal-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    timestamp: 'Ayer',
    isRead: true,
    actionUrl: '/seguir-usuarios',
  },
  {
    id: 10,
    type: 'nuevo-seguidor',
    fromUserId: 'oyente-8',
    fromName: 'María José Vargas',
    fromPhoto: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20with%20glasses%20warm%20intellectual%20style%20soft%20smile%20creative%20casual%20outfit%20coffee%20shop%20background%20editorial%20portrait%20natural%20light&width=80&height=80&seq=fn-10&orientation=squarish',
    description: 'Empezó a seguirte. Es nueva en la comunidad, ¡dale la bienvenida!',
    icon: 'ri-user-add-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    timestamp: 'Hace 2 días',
    isRead: true,
    actionUrl: '/seguir-usuarios',
  },
];

export function getUsersByType(type: FollowUserType | 'todos'): FollowUser[] {
  if (type === 'todos') return followUsers;
  return followUsers.filter((u) => u.type === type);
}

export function getUserById(id: string): FollowUser | undefined {
  return followUsers.find((u) => u.id === id);
}

export function getFollowersOfUser(userId: string): FollowUser[] {
  const user = followUsers.find((u) => u.id === userId);
  if (!user) return [];
  return followUsers.filter((u) => u.id !== userId && u.isFollowing).slice(0, 8);
}

export function getFollowingOfUser(userId: string): FollowUser[] {
  const user = followUsers.find((u) => u.id === userId);
  if (!user) return [];
  return followUsers.filter((u) => u.id !== userId && user.isFollowing).slice(0, 8);
}

export function getMostPopularUsers(limit = 8): FollowUser[] {
  return [...followUsers].sort((a, b) => b.followersCount - a.followersCount).slice(0, limit);
}

export function getLiveUsers(): FollowUser[] {
  return followUsers.filter((u) => u.isLive);
}

export function getUnreadNotifications(): FollowNotification[] {
  return followNotificationsList.filter((n) => !n.isRead);
}

export function getActivityByUser(userId: string): FollowActivityItem[] {
  return followActivity.filter((a) => a.userId === userId);
}

export function getMutualFollowers(): FollowUser[] {
  return followUsers.filter((u) => u.isFollowing);
}

// ─── SOCIAL ACTIONS: FOLLOW / UNFOLLOW / BLOCK / UNBLOCK / REPORT ────────────

export function followUser(userId: string): { success: boolean; message: string } {
  const user = followUsers.find((u) => u.id === userId);
  if (!user) return { success: false, message: 'Usuario no encontrado.' };
  if (user.isBlocked) return { success: false, message: 'No podés seguir a un usuario bloqueado. Desbloquealo primero.' };
  if (user.isFollowing) return { success: false, message: 'Ya seguís a este usuario.' };
  user.isFollowing = true;
  return { success: true, message: `Ahora seguís a ${user.name}.` };
}

export function unfollowUser(userId: string): { success: boolean; message: string } {
  const user = followUsers.find((u) => u.id === userId);
  if (!user) return { success: false, message: 'Usuario no encontrado.' };
  if (!user.isFollowing) return { success: false, message: 'No estás siguiendo a este usuario.' };
  user.isFollowing = false;
  return { success: true, message: `Dejaste de seguir a ${user.name}.` };
}

export function blockUser(userId: string): { success: boolean; message: string } {
  const user = followUsers.find((u) => u.id === userId);
  if (!user) return { success: false, message: 'Usuario no encontrado.' };
  if (user.isBlocked) return { success: false, message: 'Este usuario ya está bloqueado.' };
  user.isBlocked = true;
  user.isFollowing = false;
  return { success: true, message: `${user.name} fue bloqueado. No verás su contenido ni podrá interactuar con vos.` };
}

export function unblockUser(userId: string): { success: boolean; message: string } {
  const user = followUsers.find((u) => u.id === userId);
  if (!user) return { success: false, message: 'Usuario no encontrado.' };
  if (!user.isBlocked) return { success: false, message: 'Este usuario no está bloqueado.' };
  user.isBlocked = false;
  return { success: true, message: `${user.name} fue desbloqueado.` };
}

export function reportUser(userId: string, reason: string): { success: boolean; message: string } {
  const user = followUsers.find((u) => u.id === userId);
  if (!user) return { success: false, message: 'Usuario no encontrado.' };
  return { success: true, message: `Reporte enviado para ${user.name}. Motivo: ${reason}. Nuestro equipo de moderación lo revisará.` };
}

export function getBlockedUsers(): FollowUser[] {
  return followUsers.filter((u) => u.isBlocked);
}

export function canFollowUser(userId: string): boolean {
  const user = followUsers.find((u) => u.id === userId);
  if (!user) return false;
  return !user.isBlocked && !user.isFollowing;
}

export function canUnfollowUser(userId: string): boolean {
  const user = followUsers.find((u) => u.id === userId);
  if (!user) return false;
  return user.isFollowing;
}